import { useCallback, useEffect, useState } from 'react';
import { socialApi } from '../../support/supportApi';

/**
 * Follow state + actions for the peer shown in a message thread.
 */
export function useMessagingPeerFollow(userId, viewerUserId, followerProfileMode = 'personal') {
  const [followState, setFollowState] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const uid = String(userId || '').trim();
    const viewer = String(viewerUserId || '').trim();
    if (!uid || !viewer) {
      setFollowState(null);
      return;
    }
    if (uid === viewer) {
      setFollowState({ status: 'self', isSelf: true });
      return;
    }
    try {
      const data = await socialApi.followStatuses({
        userIds: [uid],
        followerProfileMode,
        followingProfileMode: 'personal',
      });
      setFollowState(data.states?.[uid] || { status: 'none', isSelf: false });
    } catch {
      setFollowState({ status: 'none', isSelf: false });
    }
  }, [userId, viewerUserId, followerProfileMode]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const follow = useCallback(async () => {
    const uid = String(userId || '').trim();
    if (!uid || busy) return;
    setBusy(true);
    try {
      const data = await socialApi.follow({
        userId: uid,
        followerProfileMode,
        followingProfileMode: 'personal',
      });
      setFollowState(data.followState || { status: data.status || 'active', isSelf: false });
    } finally {
      setBusy(false);
    }
  }, [userId, busy, followerProfileMode]);

  const unfollow = useCallback(async () => {
    const uid = String(userId || '').trim();
    if (!uid || busy) return;
    setBusy(true);
    try {
      const data = await socialApi.unfollow({
        userId: uid,
        followerProfileMode,
        followingProfileMode: 'personal',
      });
      setFollowState(data.followState || { status: 'none', isSelf: false });
    } finally {
      setBusy(false);
    }
  }, [userId, busy, followerProfileMode]);

  return { followState, busy, follow, unfollow, refresh };
}
